import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Shapes } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { CATEGORY_ICON_FALLBACK, Icon } from "@/ds";
import api from "@/lib/api";
import { categoryLabel } from "@/lib/catalog";
import { errorMessage } from "@/lib/errors";

/**
 * The catalog's categories and their glyphs (REG, 2026-10-29).
 *
 * `categories.icon` is the source of truth, so this is where a glyph is
 * chosen for a category added by hand, and where the `briefcase` a
 * Dany-created category fell back to gets replaced. The picker offers only
 * the whitelist the backend sends (`services/category_icons.json`); a glyph
 * another category wears is shown but disabled, because the backend refuses
 * it anyway (409 `icon_in_use`) and a button that can only fail is worse than
 * one that says so up front.
 *
 * The icon names are data, not copy: they are the button's accessible name
 * and nobody translated them.
 */

const CODES = ["icon_in_use", "icon_not_allowed", "category_exists"];

function refusal(e, t) {
  const code = e?.response?.data?.detail?.code;
  return CODES.includes(code) ? t(`adminCategories.errors.${code}`) : errorMessage(e, t("common.error"));
}

function IconPicker({ icons, taken, value, onPick, testid }) {
  return (
    <div className="grid grid-cols-6 sm:grid-cols-10 gap-2" role="radiogroup" data-testid={testid}>
      {icons.map((name) => {
        const busy = taken.has(name) && name !== value;
        const on = name === value;
        return (
          <button
            key={name}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={name}
            title={name}
            disabled={busy}
            onClick={() => onPick(name)}
            className={`h-11 w-11 rounded-md border inline-flex items-center justify-center ${
              on ? "border-hive-deep bg-hive/20" : "border-sand bg-cream hover:border-worker"
            } ${busy ? "opacity-30 cursor-not-allowed" : ""}`}
            data-testid={`${testid}-${name}`}
          >
            <Icon name={name} size={20} />
          </button>
        );
      })}
    </div>
  );
}

export default function AdminCategories() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [icons, setIcons] = useState([]);
  const [fallback, setFallback] = useState(CATEGORY_ICON_FALLBACK);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({ name: "", name_es: "", icon: "" });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/categories");
      setCategories(data.categories || []);
      setIcons(data.icons || []);
      setFallback(data.fallback || CATEGORY_ICON_FALLBACK);
      setError("");
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const taken = new Set(categories.map((c) => c.icon).filter((i) => i && i !== fallback));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/categories", draft);
      setDraft({ name: "", name_es: "", icon: "" });
      toast.success(t("common.savedSuccess"));
      await load();
    } catch (err) {
      toast.error(refusal(err, t));
    } finally {
      setBusy(false);
    }
  };

  const setIcon = async (categoryId, icon) => {
    try {
      await api.patch(`/admin/categories/${categoryId}/icon`, { icon });
      setEditing(null);
      toast.success(t("common.savedSuccess"));
      await load();
    } catch (err) {
      toast.error(refusal(err, t));
    }
  };

  const ready = draft.name.trim().length >= 2 && draft.name_es.trim().length >= 2 && draft.icon;

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-categories">
        <div className="flex items-center gap-3 mb-6">
          <Shapes size={22} className="text-hive-ink" />
          <h1 className="font-display text-3xl font-bold text-worker">{t("adminCategories.title")}</h1>
        </div>

        {error && <p className="text-danger text-sm mb-4" data-testid="admin-categories-error">{error}</p>}

        <form onSubmit={create} className="card-base p-4 max-w-3xl min-w-0 mb-6" data-testid="admin-categories-new">
          <h2 className="font-display text-lg font-bold text-worker mb-3">{t("adminCategories.new")}</h2>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <label className="text-sm text-worker">
              {t("adminCategories.nameEn")}
              <input className="input-base mt-1 w-full" value={draft.name} maxLength={60}
                     onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                     data-testid="admin-categories-name" />
            </label>
            <label className="text-sm text-worker">
              {t("adminCategories.nameEs")}
              <input className="input-base mt-1 w-full" value={draft.name_es} maxLength={60}
                     onChange={(e) => setDraft({ ...draft, name_es: e.target.value })}
                     data-testid="admin-categories-name-es" />
            </label>
          </div>
          <p className="text-sm text-worker mb-2">{t("adminCategories.icon")}</p>
          <IconPicker icons={icons} taken={taken} value={draft.icon}
                      onPick={(icon) => setDraft({ ...draft, icon })} testid="admin-categories-new-icon" />
          <button type="submit" disabled={!ready || busy}
                  className="fx-btn fx-btn--primary mt-4" data-testid="admin-categories-create">
            {t("adminCategories.create")}
          </button>
        </form>

        <div className="card-base max-w-3xl min-w-0" data-testid="admin-categories-list">
          {categories.map((c) => (
            <div key={c.category_id} className="p-4 border-t border-sand first:border-0 min-w-0"
                 data-testid={`admin-category-${c.slug}`}>
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-md bg-cream border border-sand inline-flex items-center justify-center shrink-0">
                  <Icon name={c.icon || fallback} size={20} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-worker break-words">{categoryLabel(t, c)}</p>
                  {(!c.icon || c.icon === fallback) && (
                    <p className="text-xs text-danger" data-testid="admin-category-fallback">
                      {t("adminCategories.fallbackHint")}
                    </p>
                  )}
                </div>
                <button type="button" className="fx-btn fx-btn--secondary"
                        onClick={() => setEditing(editing === c.category_id ? null : c.category_id)}
                        data-testid={`admin-category-edit-${c.slug}`}>
                  {editing === c.category_id ? t("common.cancel") : t("adminCategories.changeIcon")}
                </button>
              </div>
              {editing === c.category_id && (
                <div className="mt-3">
                  <IconPicker icons={icons} taken={taken} value={c.icon}
                              onPick={(icon) => setIcon(c.category_id, icon)}
                              testid={`admin-category-icon-${c.slug}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
